import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { darken, useTheme } from '@mui/material/styles';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import Select, { components } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import {
  ArrowSelectorIcon,
  CirclePlusIcon,
  CloseCircleFilledIcon,
} from '../icons';
import FormLabel from './FormLabel';

const MultiValue = (props) => {
  const maxToShow = props.selectProps.maxToShow || 3;

  if (props.index > maxToShow) return null;
  else if (props.index < maxToShow) {
    return <components.MultiValue {...props} />;
  }

  const length = props.getValue().length;
  const shouldBadgeShow = length > maxToShow;
  const displayLength = length - maxToShow;

  if (shouldBadgeShow) {
    const { children, ...rest } = props;
    return (
      <components.MultiValueLabel {...rest}>
        <Typography variant="caption">{`+${displayLength} more`}</Typography>
      </components.MultiValueLabel>
    );
  }
  return <components.MultiValue {...props} />;
};

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <ArrowSelectorIcon />
    </components.DropdownIndicator>
  );
};

const ClearIndicator = (props) => {
  return (
    <components.ClearIndicator {...props}>
      <CloseCircleFilledIcon fontSize="small" />
    </components.ClearIndicator>
  );
};

const getTheme = (muiTheme) => (theme) => {
  return {
    ...theme,
    borderRadius: 12,
    spacing: {
      baseUnit: 4,
      controlHeight: 40,
      menuGutter: 8,
    },
    colors: {
      ...theme.colors,
      placeholder: muiTheme.palette.text.subtle,
      danger: muiTheme.palette.error.main,
      neutral0: muiTheme.palette.input.background,
      neutral5: muiTheme.palette.background.paper5,
      neutral10: muiTheme.palette.grey['100'],
      primary: muiTheme.palette.text.primary,
      secondary: muiTheme.palette.text.secondary,
      primary25: muiTheme.palette.action.hover,
      neutral30: muiTheme.palette.grey['300'],
      neutral20: muiTheme.palette.grey['200'],
      neutral80: muiTheme.palette.text.primary,
      selected: muiTheme.palette.action.selected,
      highlight: muiTheme.palette.action.highlight,
      selectedBorder: muiTheme.palette.action.highlightBorder,
      selectedText: muiTheme.palette.action.highlightContrastText,
      focusColor: muiTheme.palette.action.highlight,
      activeColor: muiTheme.palette.input.highlight,
      borderColor: muiTheme.palette.dividerVariant,
    },
  };
};

const formatCreateLabel = (inputValue) => (
  <Stack direction="row" spacing={1}>
    <CirclePlusIcon sx={{ color: 'text.secondary' }} />
    <Typography fontWeight={500}>
      Create "
      <Typography component="span" color="input.highlight" fontWeight={500}>
        {inputValue}
      </Typography>
      "
    </Typography>
  </Stack>
);

const customStyles = {
  container: (provided) => ({
    ...provided,
    'label + &': {
      marginTop: 4,
    },
  }),
  control: (provided, { theme, selectProps }) => {
    const isOpen = selectProps.menuIsOpen;
    const height = {
      mini: 28,
      small: 32,
      medium: 36,
      large: 40,
    };
    return {
      ...provided,
      minHeight: height[selectProps.size || 'medium'],
      borderRadius: 8,
      boxShadow: null,
      opacity: selectProps.isDisabled ? 0.5 : 1,
      borderColor: selectProps.hasError
        ? theme.colors.danger
        : isOpen
          ? theme.colors.activeColor
          : theme.colors.borderColor,
      '&:hover': {
        borderColor: selectProps.hasError
          ? theme.colors.danger
          : isOpen
            ? theme.colors.activeColor
            : theme.colors.focusColor,
      },
    };
  },
  valueContainer: (provided, { selectProps }) => {
    return {
      ...provided,
      padding: 3,
      gap: 3,
      fontSize: 14,
      // paddingLeft: !selectProps.isMulti && 8,
    };
  },
  placeholder: (provided, { theme }) => ({
    ...provided,
    color: theme.colors.placeholder,
    fontWeight: 400,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
    paddingLeft: 8,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.theme.colors.secondary,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  // indicatorsContainer: (provided) => ({
  //   ...provided,
  //   padding: 0,
  // }),
  clearIndicator: (provided, state) => ({
    ...provided,
    padding: 0,
    color: state.theme.colors.danger,
    '&:hover': {
      color: darken(state.theme.colors.danger, 0.2),
    },
  }),
  menu: (provided, { theme }) => ({
    ...provided,
    zIndex: 99,
    overflow: 'hidden',
    width: 'max-content',
    minWidth: '100%',
    maxWidth: 400,
    padding: 8,
    margin: 0,
  }),
  option: (provided, { isSelected, isFocused, isDisabled, theme }) => {
    return {
      ...provided,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      backgroundColor: isSelected
        ? theme.colors.selected
        : isFocused && !isDisabled
          ? theme.colors.focusColor
          : 'transparent',
      color: isDisabled ? theme.colors.neutral20 : theme.colors.primary,
      fontSize: 14,
      borderRadius: 6,
      fontWeight: 500,
      marginBottom: 4,
      '&:last-of-type': {
        marginBottom: 0,
      },
    };
  },
  input: (provided, { theme }) => ({
    ...provided,
    padding: 0,
    paddingLeft: 4,
    margin: 0,
  }),
  singleValue: (provided, { isDisabled, theme }) => ({
    ...provided,
    color: 'inherit',
    fontWeight: 400,
    opacity: isDisabled ? 0.5 : 1,
    margin: 0,
    paddingLeft: 8,
  }),
  multiValue: (styles, { theme, selectProps }) => {
    const padding = {
      mini: '1px 8px',
      small: '3px 8px',
      medium: '5px 8px',
      large: '7px 8px',
    };
    return {
      ...styles,
      margin: 0,
      padding: padding[selectProps.size || 'medium'],
      backgroundColor: theme.colors.highlight,
      border: '1px solid',
      borderColor: theme.colors.selectedBorder,
    };
  },
  multiValueLabel: (styles, { theme, selectProps }) => ({
    ...styles,
    // padding: selectProps.size === 'small' ? 2 : 3,
    padding: 0,
    paddingLeft: 0,
    fontSize: 12,
    lineHeight: 1.3,
    color: theme.colors.selectedText,
    fontWeight: 500,
  }),
  multiValueRemove: (styles, { theme }) => ({
    ...styles,
    paddingLeft: 8,
    paddingRight: 0,
    color: theme.colors.selectedText,
    '& svg': { width: 16, height: 16 },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  }),
};

const CustomSelect = React.forwardRef(
  (
    {
      className,
      placeholder = 'Please select',
      label,
      tooltip,
      hint,
      options,
      value,
      onChange,
      hasError,
      helpText,
      loadOptions,
      paginate,
      transformValue,
      isMulti = false,
      cacheOptions = true,
      size,
      styles,
      sx,
      fullWidth,
      margin = 'normal',
      disabled,
      CustomMenu,
      isCreatable = false,
      required,
      ...rest
    },
    ref,
  ) => {
    const muiTheme = useTheme();

    const AsyncComponent = paginate ? AsyncPaginate : AsyncSelect;

    const handleChange = (value, action) => {
      if (transformValue) {
        if (isMulti) {
          onChange(
            value.map((v) => v.value),
            action,
          );
        } else {
          onChange(value?.value, action);
        }
      } else {
        onChange(value, action);
      }
    };

    const getValue = () => {
      if (transformValue && options) {
        if (isMulti) {
          return (options || []).filter((o) => (value || []).includes(o.value));
        }
        let option = null;
        options.find((i) => {
          if (i.options) {
            const isMatch = i.options.find((i) => i.value === value);
            if (isMatch) {
              option = isMatch;
            }
            return isMatch;
          }
          const isMatch = i.value === value;
          if (isMatch) {
            option = i;
          }
          return isMatch;
        });
        return option;
      } else {
        return value;
      }
    };

    const Components = {
      IndicatorSeparator: null,
      DropdownIndicator,
      ClearIndicator,
    };
    if (rest.maxToShow) {
      Components.MultiValue = MultiValue;
    }
    if (CustomMenu) {
      Components.MenuList = CustomMenu;
    }

    const SelectComponent = isCreatable ? CreatableSelect : Select;

    return (
      <FormControl
        size={size}
        variant="standard"
        margin={margin}
        className={className}
        sx={sx}
        fullWidth={fullWidth}
        error={hasError}
        required={required}
      >
        {label && <FormLabel tooltip={tooltip}>{label}</FormLabel>}
        {loadOptions ? (
          <AsyncComponent
            classNamePrefix="react-select"
            ref={ref}
            size={size}
            isDisabled={disabled}
            theme={getTheme(muiTheme)}
            className={cx('react-select', size)}
            components={Components}
            cacheOptions={cacheOptions}
            loadOptions={loadOptions}
            defaultOptions={options}
            placeholder={placeholder}
            onChange={handleChange}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            styles={customStyles}
            value={getValue()}
            hasError={hasError}
            hideSelectedOptions={!Components.MultiValue}
            {...rest}
          />
        ) : (
          <SelectComponent
            classNamePrefix="react-select"
            ref={ref}
            size={size}
            hasError={hasError}
            theme={getTheme(muiTheme)}
            className={cx('react-select', size)}
            components={Components}
            placeholder={placeholder}
            options={options}
            value={getValue()}
            onChange={handleChange}
            isDisabled={disabled}
            isMulti={isMulti}
            hideSelectedOptions={!Components.MultiValue}
            formatCreateLabel={isCreatable && formatCreateLabel}
            closeMenuOnSelect={!isMulti}
            styles={styles ? styles : customStyles}
            {...rest}
          />
        )}
        {hint && (
          <Typography
            component="p"
            variant="caption"
            color="textSecondary"
            sx={{ pl: 2, pt: 0.5 }}
          >
            {hint}
          </Typography>
        )}
        {hasError && <FormHelperText>{helpText}</FormHelperText>}
      </FormControl>
    );
  },
);

CustomSelect.propTypes = {
  placeholder: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.any }),
  ),
  value: PropTypes.any,
  onChange: PropTypes.func,
  onCreateOption: PropTypes.func,
  onInputChange: PropTypes.func,
  isMulti: PropTypes.bool,
  isCreatable: PropTypes.bool,
  loadOptions: PropTypes.func,
  transformValue: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['mini', 'small', 'medium', 'large']),
  hasError: PropTypes.bool,
  helpText: PropTypes.string,
  hint: PropTypes.string,
  tooltip: PropTypes.string,
};

export default CustomSelect;
